import React from 'react';
import { InformationCardProps, CardSvgIcon } from '../../InformationCard.types';
import {
  LinkCell,
  StatusContent,
  StatusCell,
  StatusTable,
} from './index.styles';
import { Typography } from '../../../typography';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import AdjustRoundedIcon from '@mui/icons-material/AdjustRounded';
import { ImageOrIcon } from '../ImageOrIcon';
import { StatusType } from '../../enum';
import { useStatusCarsStyles } from './index.styles';
import TableContainer from '@mui/material/TableContainer';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import PendingRoundedIcon from '@mui/icons-material/PendingRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { Validate } from '@dewise-devex/backstage-plugin-utilities-common-client';

const StatusIconMap: Record<StatusType, CardSvgIcon> = {
  [StatusType.ok]: CheckCircleRoundedIcon,
  [StatusType.error]: ErrorRoundedIcon,
  [StatusType.running]: AdjustRoundedIcon,
  [StatusType.warning]: WarningRoundedIcon,
  [StatusType.pending]: PendingRoundedIcon,
  [StatusType.aborted]: CancelRoundedIcon,
};

/**
 * CardStatus Component
 *
 * The `CardStatus` component displays a table of items with status icons, primary text,
 * and optional metadata. It leverages MUI components and supports dynamic styling
 * and rendering based on the provided data.
 *
 * @param {InformationCardProps} props - The properties for the `CardStatus` component.
 * @returns {React.ReactElement} A table element displaying the status information.
 *
 * @remarks
 * - The component maps specific status types (e.g., `ok`, `error`, `pending`) to icons.
 * - It accepts an `EmptyComponent` to render when there is no data to display.
 * - The `metadata` object can include additional information displayed in the table.
 *
 * @example
 * ```tsx
 * import { CardStatus } from './CardStatus';
 * import { StatusType } from '../../enum';
 *
 * const data = {
 *   items: [
 *     {
 *       primary: 'Task 1',
 *       secondary: 'Pending Approval',
 *       metadata: { status: StatusType.pending, extraInfo: 'Due today' },
 *     },
 *     {
 *       primary: 'Task 2',
 *       secondary: 'Completed',
 *       metadata: { status: StatusType.ok },
 *     },
 *   ],
 * };
 *
 * <CardStatus data={data} />;
 * ```
 *
 * @category InformationCard
 * @document CardStatusComponentUsage.md
 */
export const CardStatus = (props: InformationCardProps): React.ReactElement => {
  const classes = useStatusCarsStyles();

  if (props.data?.items) {
    if (Validate.isEmptyArray(props.data.items)) {
      if (props.data?.EmptyComponent) {
        return props.data.EmptyComponent;
      }
      return <Typography>No data to display</Typography>;
    }

    return (
      <TableContainer component={'div'} color="inherit">
        <StatusTable>
          <TableBody>
            {props.data.items.map((row, index) => {
              const status = row.metadata?.status;
              const icon = status ? StatusIconMap[status] : AdjustRoundedIcon;
              return (
                <TableRow key={index}>
                  <StatusCell>
                    <StatusContent>
                      {/* Displays the icon associated with the status */}
                      {icon && status && (
                        <ImageOrIcon
                          src={icon as unknown as string}
                          className={classes[status]}
                          key={`${row.primary}_td_root`}
                        />
                      )}
                      {row.primary}
                    </StatusContent>
                  </StatusCell>
                  {/* Renders secondary text with link styling if available */}
                  {row.secondary && (
                    <StatusCell>
                      <LinkCell fontSize={12}>{row.secondary}</LinkCell>
                    </StatusCell>
                  )}
                  {/* Iterates through metadata, excluding status, to display additional info */}
                  {row.metadata && (
                    <StatusCell>
                      {Object.entries(row.metadata).map(([key, value]) => {
                        if (key === 'status') {
                          return null;
                        }
                        return (
                          <Typography key={key} fontSize={12}>
                            {value}
                          </Typography>
                        );
                      })}
                    </StatusCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </StatusTable>
      </TableContainer>
    );
  } else {
    // Throw an error if `items` is missing, since `CardStatus` relies on it
    throw new Error('CardStatus component requires items prop to be passed');
  }
};
